import { Gender, Prefecture, ProgramLevel } from '@gyym/sdk'
import {
  Button,
  CalendarInputModalField,
  CheckboxGroupField,
  CheckboxGroupModalField,
  cn,
  Form,
  FormProps,
  Loading,
  SelectRangeField,
  TextField,
  TimeRangeField,
  Typography,
} from '@gyym/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import dayjs from 'dayjs'
import React from 'react'
import { useController, useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'

import { useDebounce } from '@/hooks/useDebounce'
import { useFacilitiesCount } from '@/modules/Facilities'
import {
  useSearchActivityOptions,
  useSearchAreaOptions,
} from '@/modules/FacilitySearch'
import { levelOptions, pointOptions } from '@/modules/Programs'
import { genderOptions } from '@/modules/Users'

import { ResultCount, ResultCountContainer } from './styles'

const dateRegex = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/
const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/

const schema = z
  .object({
    facilityName: z.string().nullable(),
    prefectureIds: z.array(z.nativeEnum(Prefecture)),
    areaIds: z.array(z.number()),
    activityIds: z.array(z.number()),
    sexLimitations: z.array(z.nativeEnum(Gender)),
    exerciseLevels: z.array(z.nativeEnum(ProgramLevel)),
    dates: z.array(z.string().regex(dateRegex)),
    priceFrom: z.number().nullable(),
    priceTo: z.number().nullable(),
    startTimeFrom: z
      .string()
      .regex(timeRegex, {
        message: '時間が不正です',
      })
      .nullable(),
    startTimeTo: z
      .string()
      .regex(timeRegex, {
        message: '時間が不正です',
      })
      .nullable(),
  })
  // Check priceFrom is less than priceTo
  .refine(
    ({ priceFrom, priceTo }) =>
      priceFrom === null || priceTo === null || priceFrom < priceTo,
    {
      path: ['priceTo'],
      message: '上限は下限より大きい数字を指定してください',
    },
  )
  // Check startTimeFrom is less than startTimeTo
  .refine(
    ({ startTimeFrom, startTimeTo }) => {
      if (startTimeFrom === null || startTimeTo === null) return true

      const [hourBefore, minuteBefore] = startTimeFrom.split(':')
      const [hourAfter, minuteAfter] = startTimeTo.split(':')

      const start = dayjs()
        .hour(Number(hourBefore))
        .minute(Number(minuteBefore))

      const end = dayjs().hour(Number(hourAfter)).minute(Number(minuteAfter))

      return start.isBefore(end)
    },
    {
      path: ['startTimeTo'],
      message: '時間の範囲が不正です',
    },
  )

export type FormValues = z.infer<typeof schema>

export const initialValues: FormValues = {
  facilityName: '',
  prefectureIds: [],
  areaIds: [],
  activityIds: [],
  sexLimitations: [],
  exerciseLevels: [],
  dates: [],
  priceFrom: null,
  priceTo: null,
  startTimeFrom: null,
  startTimeTo: null,
}

export interface SearchFormProps
  extends Omit<FormProps, 'onSubmit' | 'children'> {
  /**
   * Initial form values
   */
  values?: Partial<FormValues>

  /**
   * Submit handler
   */
  onSubmit: (values: FormValues) => void

  /**
   * Children
   */
  children?: ({
    fields,
    submit,
  }: {
    fields: React.ReactNode
    submit: React.ReactNode
  }) => React.ReactNode
}

export const SearchForm = ({
  values,
  onSubmit,
  children,
  ...rest
}: SearchFormProps) => {
  const {
    control,
    register,
    watch,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm({
    // Validation will trigger on the first blur event.
    // After the first blur event, validation will trigger on every change event.
    mode: 'onTouched',
    resolver: zodResolver(schema),
    defaultValues: { ...initialValues, ...values },
  })

  // Reset form when values change
  React.useEffect(() => {
    reset({ ...initialValues, ...values })
  }, [reset, values])

  // Debounce value to reduce API calls.
  // NOTE: watch() returns a new object reference on every render, which kept
  // resetting useDebounce's timer. useWatch only updates when subscribed
  // values actually change. defaultValue ensures the first render returns the
  // initial form state instead of undefined, so the count fetcher receives
  // valid params on mount.
  const watched = useWatch({
    control,
    defaultValue: { ...initialValues, ...values },
  })
  const currentFormValues = useDebounce(watched, 1000)
  const { data, isValidating: isValidatingResultCount } =
    useFacilitiesCount(currentFormValues)

  const count = data?.count ?? 0

  const { data: areaOptions } = useSearchAreaOptions({ withCount: true })
  const activityOptions = useSearchActivityOptions()

  const { field: prefectureIdsField } = useController({
    control,
    name: 'prefectureIds',
    defaultValue: values?.prefectureIds ?? [],
  })

  const { field: areaIdsField } = useController({
    control,
    name: 'areaIds',
    defaultValue: values?.areaIds ?? [],
  })

  const { field: activityIdsField } = useController({
    control,
    name: 'activityIds',
    defaultValue: values?.activityIds ?? [],
  })

  const { field: priceFromField } = useController({
    control,
    name: 'priceFrom',
    defaultValue: values?.priceFrom ?? null,
  })
  const priceFromValue = watch('priceFrom')

  const { field: priceToField } = useController({
    control,
    name: 'priceTo',
    defaultValue: values?.priceTo ?? null,
  })

  const { field: sexLimitationsField } = useController({
    control,
    name: 'sexLimitations',
    defaultValue: values?.sexLimitations ?? [],
  })

  const { field: exerciseLevelsField } = useController({
    control,
    name: 'exerciseLevels',
    defaultValue: values?.exerciseLevels ?? [],
  })

  const { field: datesField } = useController({
    control,
    name: 'dates',
    defaultValue: values?.dates ?? [],
  })

  const { field: startTimeFromField } = useController({
    control,
    name: 'startTimeFrom',
    defaultValue: values?.startTimeFrom ?? null,
  })

  const { field: startTimeToField } = useController({
    control,
    name: 'startTimeTo',
    defaultValue: values?.startTimeTo ?? null,
  })

  const fields = (
    <>
      <Typography as="div" variant="headline3">
        基本条件
      </Typography>

      <TextField
        mt={4}
        label="施設名"
        placeholder="施設名を入力"
        {...register('facilityName')}
        errorMessages={errors.facilityName?.message}
      />

      <CheckboxGroupModalField
        mt={4}
        label="エリア"
        options={areaOptions}
        value={{
          parentValues: prefectureIdsField.value,
          childValues: areaIdsField.value,
        }}
        onChange={({ parentValues, childValues }) => {
          prefectureIdsField.onChange(
            // Filter non-existent prefecture IDs
            parentValues.filter((v) => areaOptions.some((o) => o.value === v)),
          )

          areaIdsField.onChange(
            // Filter non-existent area IDs (from nested children)
            childValues.filter((v) =>
              areaOptions.some((o) => o.children?.some((c) => c.value === v)),
            ),
          )
        }}
        errorMessages={
          [errors.prefectureIds?.message, errors.areaIds?.message].filter(
            (v) => v,
          ) as string[]
        }
      />

      <CheckboxGroupModalField
        mt={4}
        label="アクティビティ"
        options={activityOptions}
        value={{
          parentValues: activityIdsField.value,
        }}
        onChange={({ parentValues }) => {
          activityIdsField.onChange(
            // Filter non-existent activity IDs
            parentValues.filter((v) =>
              activityOptions.some((o) => o.value === v),
            ),
          )
        }}
        errorMessages={errors.activityIds?.message}
      />

      <Typography as="div" variant="headline3" mt={6}>
        利用日時
      </Typography>

      <CalendarInputModalField
        mt={4}
        label="日付"
        mode="multiple"
        fromDate={dayjs().toDate()}
        toDate={dayjs().add(2, 'week').toDate()}
        value={datesField.value.map((date) => dayjs(date).toDate())}
        onChange={(dates) => {
          datesField.onChange(
            dates.map((date) => dayjs(date).format('YYYY-MM-DD')),
          )
        }}
        errorMessages={errors.dates?.message}
      />

      <TimeRangeField
        mt={2}
        label="開始時間"
        value={{
          from: startTimeFromField.value,
          to: startTimeToField.value,
        }}
        onBlurFrom={startTimeFromField.onBlur}
        onBlurTo={startTimeToField.onBlur}
        onChange={({ from, to }) => {
          startTimeFromField.onChange(from)
          startTimeToField.onChange(to)
        }}
        errorMessages={
          [errors.startTimeFrom?.message, errors.startTimeTo?.message].filter(
            (v) => v,
          ) as string[]
        }
      />

      <Typography as="div" variant="headline3" mt={6}>
        詳細条件
      </Typography>

      <SelectRangeField
        label="ポイント"
        mt={4}
        options={[
          {
            label: '未選択',
            value: null,
          },
          ...pointOptions,
        ]}
        toOptions={
          // Filter options by priceFrom
          priceFromValue
            ? pointOptions.filter((v) => v.value > priceFromValue)
            : undefined
        }
        fromPlaceholder="下限"
        toPlaceholder="上限"
        toDisabled={
          // Disable priceTo if priceFrom is equal or greater than the maximum value
          priceFromValue
            ? (
                pointOptions[pointOptions.length - 1] as {
                  label: string
                  value: number
                }
              ).value <= priceFromValue
            : false
        }
        value={{
          from: priceFromField.value,
          to: priceToField.value,
        }}
        onBlurFrom={priceFromField.onBlur}
        onBlurTo={priceToField.onBlur}
        onChange={({ from, to }) => {
          priceFromField.onChange(from)

          if (from && to && from >= to) {
            priceToField.onChange(null)
          } else {
            priceToField.onChange(to)
          }
        }}
        errorMessages={
          [errors.priceFrom?.message, errors.priceTo?.message].filter(
            (v) => v,
          ) as string[]
        }
      />

      <CheckboxGroupField
        label="専用プログラム"
        mt={4}
        options={genderOptions}
        {...sexLimitationsField}
        errorMessages={errors.sexLimitations?.message}
      />

      <CheckboxGroupField
        label="運動レベル"
        mt={6}
        options={levelOptions}
        {...exerciseLevelsField}
        errorMessages={errors.exerciseLevels?.message}
      />

      {/* TODO: Handle loading */}
      {isValidatingResultCount ? (
        <Loading mt={10} size="sm" />
      ) : (
        <ResultCountContainer className="mt-10 flex-col justify-center">
          <div className="flex items-end justify-center">
            <ResultCount>{count}</ResultCount>
            <Typography
              variant="body2"
              color="gray.01"
              as="div"
              className="mb-1 ml-1 tablet:mb-2"
            >
              件
            </Typography>
          </div>
          <div className="flex justify-center">
            <Typography
              variant="body2"
              color="gray.01"
              as="div"
              textAlign="center"
            >
              の施設があります
            </Typography>
          </div>
        </ResultCountContainer>
      )}

      <div className="mt-3 flex items-end justify-center">
        <Button
          variant="primary-text"
          onClick={() =>
            reset(initialValues, {
              keepDefaultValues: true,
            })
          }
        >
          選択条件を全消去
        </Button>
      </div>
    </>
  )

  const hasChildren = !!children

  const submit = (
    <Button
      className={cn({
        'mt-6': !hasChildren,
      })}
      type="submit"
      variant="primary"
      size="lg"
      fullWidth
      disabled={!isDirty}
    >
      この内容で検索
    </Button>
  )

  return (
    <Form
      {...rest}
      onSubmit={handleSubmit((values) => {
        onSubmit(values)
        reset(values, { keepValues: true, keepDirty: false })
      })}
    >
      {hasChildren ? (
        children({ fields, submit })
      ) : (
        <>
          {fields}
          {submit}
        </>
      )}
    </Form>
  )
}
